import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Button,
} from "@mui/material";
import Sidebar from "./SideBar";
import MenuUsuario from "./MenuUsuario";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';

// Aún no hay backend de notificaciones. Hasta que exista, no mostramos datos
// inventados: la lista va vacía y la campana no marca un contador falso.
const notifications = [];

const AppBarComponent = ({ onSidebarToggle, sidebarOpen }) => {
  const [notifAnchor, setNotifAnchor] = useState(null);
  const notifOpen = Boolean(notifAnchor);
  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: (theme) => theme.palette.secondary.main,
          color: "#ffffff",
          borderBottom: "none",
          width: "100%",
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.drawer + 2,
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", minHeight: "64px !important", px: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              onClick={onSidebarToggle}
              size="small"
              sx={{
                color: "#ffffff",
                borderRadius: "8px",
                width: 36,
                height: 36,
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.18)",
                },
              }}
            >
              {sidebarOpen ? (
                <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
              ) : (
                <ArrowForwardIosIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#ffffff",
                fontSize: "1.1rem",
                letterSpacing: "-0.01em",
              }}
            >
              Bienvenido a{" "}
              <Box component="span" sx={{ fontWeight: 800, color: "#ffffff" }}>
                Voras
              </Box>
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            {/* Campana de notificaciones */}
            <IconButton
              onClick={(e) => setNotifAnchor(e.currentTarget)}
              size="small"
              sx={{
                borderRadius: "10px",
                width: 40,
                height: 40,
                transition: "all 0.2s",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
              }}
            >
              <Badge
                badgeContent={unreadCount}
                color="error"
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "0.65rem",
                    height: 18,
                    minWidth: 18,
                  },
                }}
              >
                <NotificationsNoneIcon sx={{ fontSize: 22, color: "#ffffff" }} />
              </Badge>
            </IconButton>

            <Popover
              open={notifOpen}
              anchorEl={notifAnchor}
              onClose={() => setNotifAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  width: 360,
                  borderRadius: "12px",
                  mt: 1,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                  overflow: "hidden",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2.5,
                  py: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: "1rem" }}>
                  Notificaciones
                </Typography>
                {unreadCount > 0 && (
                  <Box
                    sx={{
                      backgroundColor: "#E8F0FE",
                      color: "#2563EB",
                      borderRadius: "12px",
                      px: 1.2,
                      py: 0.2,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                    }}
                  >
                    {unreadCount} nuevas
                  </Box>
                )}
              </Box>
              <Divider />
              {notifications.length === 0 && (
                <Box sx={{ px: 2.5, py: 5, textAlign: "center" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569" }}>
                    No tenés notificaciones
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                    Te avisaremos acá cuando haya novedades.
                  </Typography>
                </Box>
              )}
              <List sx={{ p: 0, maxHeight: 320, overflowY: "auto" }}>
                {notifications.map((notif, index) => (
                  <React.Fragment key={notif.id}>
                    <ListItem
                      sx={{
                        px: 2.5,
                        py: 1.5,
                        backgroundColor: notif.read ? "transparent" : "#f8f9ff",
                        transition: "background-color 0.2s",
                        cursor: "pointer",
                        "&:hover": { backgroundColor: "#f0f4ff" },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            backgroundColor: notif.read ? "#f5f5f5" : "#E8F0FE",
                          }}
                        >
                          {notif.icon}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: notif.read ? 400 : 600, fontSize: "0.85rem" }}
                          >
                            {notif.title}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" sx={{ color: "#666", display: "block" }}>
                              {notif.description}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#999", fontSize: "0.7rem" }}>
                              {notif.time}
                            </Typography>
                          </Box>
                        }
                      />
                      {!notif.read && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: "#2563EB",
                            flexShrink: 0,
                            ml: 1,
                          }}
                        />
                      )}
                    </ListItem>
                    {index < notifications.length - 1 && (
                      <Divider variant="inset" component="li" />
                    )}
                  </React.Fragment>
                ))}
              </List>
              {notifications.length > 0 && (
                <>
                  <Divider />
                  <Box sx={{ p: 1.5, display: "flex", justifyContent: "center" }}>
                    <Button
                      size="small"
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.8rem",
                        borderRadius: "8px",
                        color: (theme) => theme.palette.primary.main,
                      }}
                    >
                      Ver todas las notificaciones
                    </Button>
                  </Box>
                </>
              )}
            </Popover>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 32, alignSelf: "center" }} />

            <MenuUsuario />
          </Box>
        </Toolbar>
      </AppBar>

      <Sidebar open={sidebarOpen} onClose={() => onSidebarToggle(false)} />
    </>
  );
};

export default AppBarComponent;